import { useState, useEffect } from "react";
import type { ToastType } from "@legal-500/legal500-design-system";
import LoadingState from "@/components/LoadingState";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import {
  campaignsService,
  type CampaignRecipientsResult,
  type RecipientFacets,
  type RecipientRegionFilter,
  type RecipientResponseState,
} from "@/api/campaigns.service";
import { RecipientFilters } from "./RecipientFilters";
import { RecipientLegend } from "./RecipientLegend";
import { buildRecipientColumns, type RecipientRow } from "./recipient-columns";
import { SelectAllBanner } from "./SelectAllBanner";
import { SelectionActionBar } from "./SelectionActionBar";
import { SelectedRecipientsModal } from "./SelectedRecipientsModal";
import { ResendActionBar } from "./ResendActionBar";
import {
  FAILED_STATUS_HINT,
  RESENDABLE_MERGED_STATUS,
  RESENDABLE_SEND_STATUS,
  isSegmentActive,
  mergedFilterToParams,
  responseStateOf,
  type MergedRecipientStatus,
} from "./recipient-status";
import type { RecipientSelection } from "./selection";
import styles from "./MailingGroups.module.css";

const PAGE_SIZE = 25;

// The API caps `limit` at 500; the resend "select all matching" pages through
// the whole failed set in these chunks (the guard bounds a pathological loop).
const SELECT_ALL_PAGE = 500;

type Props = {
  campaignId: string;
  onSelectionChange: (selection: RecipientSelection) => void;
  onCreateSegment: () => void;
  onToast: (message: string, type: ToastType) => void;
  onTotalChange?: (total: number) => void;
  refreshKey: number;
};

export function RecipientsProjection({
  campaignId,
  onSelectionChange,
  onCreateSegment,
  onToast,
  onTotalChange,
  refreshKey,
}: Props) {
  const [result, setResult] = useState<CampaignRecipientsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Applied filters (sent to the API); all apply immediately on change. One
  // merged status filter drives whichever backend param fits (projection
  // `status` vs real `sendStatus`). Default to `new` so "select all matching"
  // targets only available recipients, not people already scheduled/sent.
  const [statusFilter, setStatusFilter] = useState<MergedRecipientStatus | undefined>("new");
  const [firmRef, setFirmRef] = useState("");
  const [regionFilter, setRegionFilter] = useState<RecipientRegionFilter | undefined>(undefined);
  // Survey-response filter — orthogonal to status; AND-combined server-side.
  const [responseFilter, setResponseFilter] = useState<RecipientResponseState | undefined>(
    undefined,
  );

  // Free-text search. `search` drives the controlled input (updates per
  // keystroke); `debouncedSearch` is what actually hits the API so typing
  // doesn't fire a request per character.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Distinct firms/regions in the projection, for the filter dropdowns.
  const [facets, setFacets] = useState<RecipientFacets>({ regions: [], firms: [] });
  // Create-mode selection is ALWAYS an explicit, ordinary set of emails — "select
  // all matching" materializes the matching set up front (see
  // `selectAllCreateMatching`) rather than switching into a live filter mode, so
  // it survives filter changes and any row can still be unchecked.
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [materializingSelectAll, setMaterializingSelectAll] = useState(false);

  // "View selected" modal — reviews/removes hand-picked emails, some of which may
  // not be visible under the current filter now that explicit picks accumulate.
  const [showSelected, setShowSelected] = useState(false);

  // --- Resend-failed selection (recipient_id-keyed, mode-switched by the
  // "Failed" send-status filter). Explicit set → exact count, no excluded model.
  const [resendIds, setResendIds] = useState<Set<string>>(new Set());
  const [resendAllMatching, setResendAllMatching] = useState(false);
  const [confirmingResend, setConfirmingResend] = useState(false);
  // Bumped after a resend to re-run the list query (statuses have changed).
  const [localRefresh, setLocalRefresh] = useState(0);

  // Resend mode is on while the operator is looking at the Failed set — the
  // whole selection/action surface flips from "create send" to "resend".
  const resendMode = statusFilter === RESENDABLE_MERGED_STATUS;

  useEffect(() => {
    let cancelled = false;
    campaignsService
      .getRecipientFacets(campaignId)
      .then(res => {
        if (!cancelled) setFacets(res);
      })
      .catch(err => {
        // Facets are optional (filter dropdowns): log and degrade to empty rather than fail the page.
        console.error("Failed to load recipient facets:", err);
        if (!cancelled) setFacets({ regions: [], firms: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  // Debounce the search term (~350ms) before it reaches the query effect below.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = mergedFilterToParams(statusFilter);
    campaignsService
      .getRecipients(campaignId, {
        limit: PAGE_SIZE,
        offset,
        status: params.status,
        sendStatus: params.sendStatus,
        sendActive: params.sendActive,
        firmRef,
        ...regionFilter,
        search: debouncedSearch,
        responseState: responseFilter,
      })
      .then(res => {
        if (!cancelled) setResult(res);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load recipients.");
          setResult({ recipients: [], total: 0, limit: PAGE_SIZE, offset });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    campaignId,
    offset,
    statusFilter,
    firmRef,
    regionFilter,
    responseFilter,
    debouncedSearch,
    refreshKey,
    localRefresh,
  ]);

  // A parent refresh (e.g. after a send is created) clears BOTH selections.
  useEffect(() => {
    setSelection(new Set());
    setResendIds(new Set());
    setResendAllMatching(false);
  }, [refreshKey]);

  const rows: RecipientRow[] = (result?.recipients ?? []).map(r => ({ ...r, id: r.email }));
  const total = result?.total ?? 0;

  // The Candidates tab badge reflects the UNFILTERED campaign pool, not the
  // filtered total, so it never jumps while the operator narrows the list. Fetch
  // it once per campaign (and on refresh, since freezing recipients changes the
  // pool); the filtered `total` above stays internal to the pager/selection.
  useEffect(() => {
    let cancelled = false;
    campaignsService
      .getRecipients(campaignId, { limit: 1, offset: 0 })
      .then(res => {
        if (!cancelled) onTotalChange?.(res.total);
      })
      .catch(err => {
        // Non-fatal: log and leave the badge at its last/zero value.
        console.error("Failed to load recipient total:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, refreshKey, localRefresh, onTotalChange]);

  // ------------------------------------------------------------------ create
  const isRowSelected = (email: string) => selection.has(email);

  // A recipient row already exists for this email in this campaign (any
  // send_status) — per-campaign dedup means a new send would silently skip them,
  // so lock the checkbox rather than let staff build a segment that "sends 0".
  // A fully-responded referee has no unanswered survey links, so the send worker
  // skips them regardless of campaign type — lock their checkbox so staff aren't
  // misled into thinking a send reaches them (they stay visible in the list).
  // Opted-out / undeliverable emails (whole-email suppression or hard bounce) are
  // likewise excluded from any send, so lock them too.
  const isCreateSelectable = (row: RecipientRow): boolean =>
    !row.recipient_id && responseStateOf(row) !== "full" && !row.opted_out;
  const selectablePageEmails = rows.filter(isCreateSelectable).map(r => r.email);

  const allOnPageSelected =
    selectablePageEmails.length > 0 && selectablePageEmails.every(isRowSelected);
  const selectedCount = selection.size;

  // ------------------------------------------------------------------ resend
  // A row is resend-eligible only when it has actually failed AND the segment that
  // claimed it is no longer active — campaign-api refuses to resend into an
  // in-flight segment, so locking those here pre-empts a `skipped` result instead
  // of letting the operator select rows the API will silently drop.
  const isResendEligible = (row: RecipientRow): boolean =>
    row.send_status === RESENDABLE_SEND_STATUS &&
    !isSegmentActive(row.segment_status) &&
    !!row.recipient_id;

  const eligiblePageIds = rows.filter(isResendEligible).map(r => r.recipient_id as string);
  const allEligiblePageSelected =
    eligiblePageIds.length > 0 && eligiblePageIds.every(id => resendIds.has(id));

  const clearResend = () => {
    setResendIds(new Set());
    setResendAllMatching(false);
  };

  const toggleResendRow = (row: RecipientRow) => {
    const id = row.recipient_id;
    if (!id) return;
    setResendIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setResendAllMatching(false);
  };

  const toggleResendPage = () => {
    setResendIds(prev => {
      const next = new Set(prev);
      for (const id of eligiblePageIds) {
        if (allEligiblePageSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    setResendAllMatching(false);
  };

  // Escalate the page selection to EVERY failed recipient matching the filter:
  // page server-side through the whole set collecting eligible recipient ids
  // (the resend endpoint takes explicit ids, so we resolve them up front).
  const selectAllResendMatching = async () => {
    try {
      const ids: string[] = [];
      let off = 0;
      for (let guard = 0; guard < 200; guard++) {
        const res = await campaignsService.getRecipients(campaignId, {
          limit: SELECT_ALL_PAGE,
          offset: off,
          sendStatus: RESENDABLE_SEND_STATUS,
          firmRef,
          ...regionFilter,
        });
        for (const r of res.recipients) {
          // Same eligibility rule as the per-row checkbox: skip rows whose claiming
          // segment is still active, or the API would just report them as `skipped`.
          if (r.recipient_id && !isSegmentActive(r.segment_status)) ids.push(r.recipient_id);
        }
        off += res.recipients.length;
        if (res.recipients.length === 0 || off >= res.total) break;
      }
      setResendIds(new Set(ids));
      setResendAllMatching(true);
    } catch (err) {
      onToast(
        err instanceof Error ? err.message : "Failed to select all failed recipients.",
        "error",
      );
    }
  };

  // Escalate the page selection to EVERY create-eligible recipient matching the
  // CURRENTLY APPLIED filters: page server-side through the whole matching set,
  // collecting selectable emails, and union them into whatever is already
  // hand-picked. Unlike resend's ids, this materializes into the same ordinary
  // explicit `selection` set — from here on it's just emails, no live filter mode.
  const selectAllCreateMatching = async () => {
    if (materializingSelectAll) return;
    setMaterializingSelectAll(true);
    try {
      const params = mergedFilterToParams(statusFilter);
      const emails: string[] = [];
      let off = 0;
      let exhausted = true;
      for (let guard = 0; guard < 200; guard++) {
        const res = await campaignsService.getRecipients(campaignId, {
          limit: SELECT_ALL_PAGE,
          offset: off,
          status: params.status,
          sendStatus: params.sendStatus,
          sendActive: params.sendActive,
          firmRef,
          ...regionFilter,
          search: debouncedSearch,
          responseState: responseFilter,
        });
        for (const r of res.recipients) {
          const row = { ...r, id: r.email };
          if (isCreateSelectable(row)) emails.push(r.email);
        }
        off += res.recipients.length;
        if (res.recipients.length === 0 || off >= res.total) {
          exhausted = false;
          break;
        }
      }
      if (exhausted) {
        onToast(
          `Only the first ${off.toLocaleString()} matching recipients were selected — narrow the filter to select the rest.`,
          "warning",
        );
      }
      setSelection(prev => new Set([...prev, ...emails]));
    } catch (err) {
      onToast(
        err instanceof Error ? err.message : "Failed to select all matching recipients.",
        "error",
      );
    } finally {
      setMaterializingSelectAll(false);
    }
  };

  const handleResendConfirm = async () => {
    const ids = [...resendIds];
    if (ids.length === 0) return;
    const { resent, skipped } = await campaignsService.resendRecipients(campaignId, ids);
    let message = `Re-sending ${resent.toLocaleString()} recipient${resent === 1 ? "" : "s"}.`;
    if (skipped > 0) {
      message += ` ${skipped.toLocaleString()} skipped — their segment is currently sending or scheduled.`;
    }
    onToast(message, skipped > 0 ? "info" : "success");
    setConfirmingResend(false);
    clearResend();
    // Re-run the list: resent rows flip submit_failed → pending and leave the
    // Failed filter, so the operator watches the failed count shrink.
    setLocalRefresh(n => n + 1);
  };

  // Report the current selection whenever it changes, so the parent can hand it
  // to the create-send modal. Create-mode selection is always explicit now.
  useEffect(() => {
    onSelectionChange({ mode: "explicit", emails: [...selection] });
  }, [selection, onSelectionChange]);

  const toggleRow = (email: string) => {
    setSelection(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const togglePage = () => {
    setSelection(prev => {
      const next = new Set(prev);
      for (const email of selectablePageEmails) {
        if (allOnPageSelected) next.delete(email);
        else next.add(email);
      }
      return next;
    });
  };

  const clearSelection = () => setSelection(new Set());

  const applyFilter = (fn: () => void) => {
    fn();
    setOffset(0);
    // Hand-picked (explicit) selections persist across filter changes so operators
    // can accumulate people found under different filters. Create-mode selection
    // is always explicit now, so it's never reset here.
    // Same rule for the resend selection: `resendIds` holds concrete recipient ids
    // (resolved up front even in "all matching" mode) and is posted verbatim, so it
    // survives. Only the "all matching" label resets — once the filter moves it no
    // longer describes what is selected.
    setResendAllMatching(false);
  };

  const columns = resendMode
    ? buildRecipientColumns({
        selectionMode: "resend",
        allOnPageSelected: allEligiblePageSelected,
        headerDisabled: eligiblePageIds.length === 0,
        isRowSelected: row => !!row.recipient_id && resendIds.has(row.recipient_id),
        isRowSelectable: isResendEligible,
        onTogglePage: toggleResendPage,
        onToggleRow: toggleResendRow,
      })
    : buildRecipientColumns({
        selectionMode: "create",
        allOnPageSelected,
        headerDisabled: selectablePageEmails.length === 0,
        isRowSelected: row => isRowSelected(row.email),
        isRowSelectable: isCreateSelectable,
        onTogglePage: togglePage,
        onToggleRow: row => toggleRow(row.email),
      });

  const rowSelected = (row: RecipientRow): boolean =>
    resendMode ? !!row.recipient_id && resendIds.has(row.recipient_id) : isRowSelected(row.email);

  return (
    <section className={styles.campaignsSection}>
      <h2 className={styles.sectionHeading}>Candidates</h2>

      <RecipientFilters
        facets={facets}
        firmRef={firmRef}
        region={regionFilter}
        statusFilter={statusFilter}
        responseFilter={responseFilter}
        search={search}
        onFirmChange={value => applyFilter(() => setFirmRef(value))}
        onRegionChange={value => applyFilter(() => setRegionFilter(value))}
        onStatusChange={value => applyFilter(() => setStatusFilter(value))}
        onResponseChange={value => applyFilter(() => setResponseFilter(value))}
        onSearchChange={value => applyFilter(() => setSearch(value))}
      />

      <RecipientLegend />

      {resendMode ? (
        <SelectAllBanner
          selectAllActive={resendAllMatching}
          allOnPageSelected={allEligiblePageSelected}
          pageCount={eligiblePageIds.length}
          total={total}
          excludedCount={0}
          onSelectAllMatching={() => void selectAllResendMatching()}
          onClear={clearResend}
        />
      ) : (
        <SelectAllBanner
          selectAllActive={false}
          allOnPageSelected={allOnPageSelected}
          pageCount={selectablePageEmails.length}
          total={total}
          excludedCount={0}
          onSelectAllMatching={() => void selectAllCreateMatching()}
          onClear={clearSelection}
        />
      )}

      {error && <p className={styles.mutedNote}>Recipients unavailable: {error}</p>}

      {loading && !result ? (
        <LoadingState message="Loading recipients..." />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={rows}
              columns={columns}
              rowKey="id"
              emptyMessage="No recipients found."
              rowClassName={row => (rowSelected(row) ? styles.selectedRow : "")}
            />
          </div>
          <PaginationControls
            currentPage={Math.floor(offset / PAGE_SIZE) + 1}
            totalPages={Math.ceil(total / PAGE_SIZE)}
            startIndex={offset}
            endIndex={offset + rows.length}
            totalCount={total}
            itemName="candidates"
            onPageChange={page => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}

      {resendMode ? (
        <ResendActionBar
          count={resendIds.size}
          busy={confirmingResend}
          onClear={clearResend}
          onResend={() => setConfirmingResend(true)}
        />
      ) : (
        <SelectionActionBar
          count={selectedCount}
          subtext="· hand-picked"
          onView={() => setShowSelected(true)}
          onClear={clearSelection}
          onCreate={onCreateSegment}
        />
      )}

      {showSelected && (
        <SelectedRecipientsModal
          emails={[...selection]}
          onRemove={email => toggleRow(email)}
          onClearAll={() => {
            clearSelection();
            setShowSelected(false);
          }}
          onClose={() => setShowSelected(false)}
        />
      )}

      {confirmingResend && (
        <ConfirmationModal
          title="Resend failed recipients"
          message={`Re-send to ${resendIds.size.toLocaleString()} failed recipient${
            resendIds.size === 1 ? "" : "s"
          }?`}
          warningMessage={`${FAILED_STATUS_HINT} Their send segment(s) will be re-run — only the previously failed sends are re-attempted; already-delivered recipients are never touched.`}
          confirmButtonText="Resend"
          cancelButtonText="Cancel"
          onClose={() => setConfirmingResend(false)}
          onConfirm={handleResendConfirm}
        />
      )}
    </section>
  );
}
